import styled from '@emotion/styled';
import { CircularProgress, TextField } from '@mui/material';
import { EmptyCon, OrderButton } from 'components/atoms';
import { Modal } from 'components/molecules';
import renderIcon from 'icon';
import moment from 'moment';
import QUERY_KEYS from 'network/config/queryKeys';
import { cancelOrder, getSingleOrder } from 'network/services/order';
import { enqueueSnackbar } from 'notistack';
import React, { useState } from 'react';
import { IoArrowBackCircleSharp } from 'react-icons/io5';
import { useMutation, useQuery } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { userRoleAtom } from 'store/atoms/userAtom';
import { COLORS } from 'theme/color';
import { formatCurrency } from 'utils/currencyFormater';
import { formatDate } from 'utils/dateFormater';

const OrderDetailsContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #f0f0f0;
`;

const BackButton = styled.div`
  cursor: pointer;
  color: #333;
  transition: color 0.2s;

  &:hover {
    color: #666;
  }
`;

const OrderTitle = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;

  h1 {
    font-size: 24px;
    font-weight: 700;
    margin: 0;
    color: #333;
  }

  .order-meta {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
  }
`;

const StatusBadge = styled.span<{ status: string }>`
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;

  ${({ status }) => {
    switch (status?.toUpperCase()) {
      case 'PENDING':
        return 'background: #fff8e1; color: #f57c00;';
      case 'ONGOING':
      case 'APPROVED':
        return 'background: #e3f2fd; color: #1976d2;';
      case 'COMPLETED':
        return 'background: #e8f5e8; color: #2e7d32;';
      case 'CANCELLED':
      case 'REJECTED':
        return 'background: #feebee; color: #c62828;';
      default:
        return 'background: #f5f5f5; color: #757575;';
    }
  }}
`;

const InfoCard = styled.div`
  background: #fafafa;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 12px 0;
  color: #333;
`;

const ProductList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 24px;
`;

const ProductItem = styled.div`
  display: flex;
  gap: 12px;
  padding: 12px;
  background: #fff;
  border-radius: 8px;
  border: 1px solid #f0f0f0;
`;

const ProductImage = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;
  background: #f5f5f5;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ProductInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const ProductName = styled.div`
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
`;

const ProductDetails = styled.div`
  font-size: 14px;
  color: #666;
`;

const ProductPrice = styled.div`
  font-weight: 700;
  color: #333;
  font-size: 16px;
  text-align: right;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-bottom: 24px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const InfoSection = styled.div`
  .info-row {
    display: flex;
    justify-content: space-between;
    margin-bottom: 8px;

    .label {
      color: #666;
      font-size: 14px;
    }

    .value {
      color: #333;
      font-weight: 500;
      font-size: 14px;
      text-align: right;
    }
  }

  .total-row {
    border-top: 1px solid #f0f0f0;
    padding-top: 8px;
    margin-top: 8px;

    .value {
      font-weight: 700;
      font-size: 16px;
      color: #333;
    }
  }
`;

const AddressInfo = styled.div`
  background: #f8f9fa;
  padding: 12px;
  border-radius: 6px;
  margin-top: 8px;
  font-size: 14px;
  color: #333;
  line-height: 1.4;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;
`;

export const OrderDetails = () => {
  const go = useNavigate();
  const { id } = useParams();
  const userRole = useRecoilValue(userRoleAtom);
  const [isDecline, setDecline] = useState<boolean>(false);
  const [err, setreasonError] = useState<string>('');
  const [reason, setReason] = React.useState('');

  const { mutateAsync: cancel } = useMutation({
    mutationFn: cancelOrder,
    onSuccess: _data => {
      enqueueSnackbar(`Order ${id} cancelled successfully.`, {
        variant: 'success'
      });
      setDecline(false);
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || error?.message || `Failed to cancel order #${id}`;
      enqueueSnackbar(errorMessage, { variant: 'error' });
      setDecline(false);
    }
  });

  const {
    data: order,
    isFetchedAfterMount,
    isLoading,
    error
  } = useQuery({
    queryKey: [QUERY_KEYS.SINGLE_ORDER, id],
    queryFn: () => getSingleOrder(Number(id)),
    enabled: !!id && !isNaN(Number(id)), // Enable the query only when we have a valid ID
    // Cache configuration
    staleTime: 5 * 60 * 1000, // 5 minutes - data stays fresh for 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes - data stays in cache for 10 minutes after component unmounts
    // Refetch configuration
    refetchOnWindowFocus: false, // Don't refetch when window regains focus
    refetchOnMount: 'always', // Always refetch when component mounts, even if data is fresh
    refetchInterval: false, // Don't auto-refetch at intervals
    // Error retry configuration
    retry: 2, // Retry failed requests 2 times
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000) // Exponential backoff
  });

  // Handle invalid id after hooks
  if (!id || isNaN(Number(id))) {
    return <EmptyCon>Invalid order ID</EmptyCon>;
  }

  if (error) {
    return <EmptyCon>Failed to load order details</EmptyCon>;
  }

  const handleBack = () => go(-1);
  const track = () => go('track', { state: { orderRef: order?.orderRef } });

  const handleCancel = () => {
    if (reason.length < 2) {
      setreasonError('Please Enter a Reason');
    } else {
      cancel({ id: Number(id), reason: reason });
    }
  };

  const calculatePrice = (price: any, quantity: any, productIndex?: number) => {
    // If price is 0 or invalid, calculate from totalAmount
    if (!price || price === 0) {
      const totalQty = order?.products?.reduce((sum: number, p: any) => sum + (p?.quantity || 0), 0) || 1;
      const unitPrice = (order?.totalAmount || 0) / totalQty;
      const amount = unitPrice * quantity;
      return formatCurrency(amount);
    }
    const amount = price * quantity;
    return formatCurrency(amount);
  };

  const getStatusDisplay = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'ORDER_PROCESSED':
        return 'ORDER PROCESSED';
      default:
        return status?.toUpperCase() || 'UNKNOWN';
    }
  };

  const canCancelOrder = () => {
    const status = order?.orderStatus?.toUpperCase();
    return status === 'PENDING' || status === 'APPROVED';
  };

  if (!isFetchedAfterMount) {
    return (
      <EmptyCon>
        <CircularProgress size={30} />
      </EmptyCon>
    );
  }

  if (!order) {
    return <EmptyCon>Order not found</EmptyCon>;
  }

  return (
    <>
      <OrderDetailsContainer>
        <Header>
          {renderIcon(IoArrowBackCircleSharp, {
            size: 24,
            className: 'text-black flex-shrink-0 cursor-pointer hover:text-gray-600 transition-colors',
            onClick: handleBack
          })}
          <OrderTitle>
            <h1>Order {order?.orderRef || 'N/A'}</h1>
            <div className="order-meta">
              <StatusBadge status={order?.orderStatus || ''}>{getStatusDisplay(order?.orderStatus || '')}</StatusBadge>
              <span style={{ color: '#666', fontSize: '14px' }}>
                {order?.orderDate ? formatDate(order.orderDate) : 'Date not available'}
              </span>
              <span style={{ color: '#666', fontSize: '14px' }}>
                {order?.products?.length || 0} item{(order?.products?.length || 0) !== 1 ? 's' : ''}
              </span>
            </div>
          </OrderTitle>
        </Header>

        {/* Products */}
        <SectionTitle>Order Items</SectionTitle>
        <ProductList>
          {Array.isArray(order?.products) &&
            order?.products?.map((item, index) => (
              <ProductItem key={item?.productId || index}>
                <ProductImage>
                  <img src={item?.images?.[0] || '/graphics/placeholder.svg'} alt={item?.productName || 'Product'} />
                </ProductImage>
                <ProductInfo>
                  <div>
                    <ProductName>{item?.productName || 'Unknown Product'}</ProductName>
                    <ProductDetails>
                      Quantity: {item?.quantity || 0}
                      {item?.description && (
                        <div style={{ marginTop: '4px', fontSize: '13px' }}>
                          {item.description.replace(/<[^>]*>/g, '')}
                        </div>
                      )}
                    </ProductDetails>
                  </div>
                </ProductInfo>
                <ProductPrice>
                  {userRole === 'BUSINESS'
                    ? calculatePrice(item?.bulkOrderPrice || 0, item?.quantity || 0)
                    : calculatePrice(item?.price || 0, item?.quantity || 0)}
                </ProductPrice>
              </ProductItem>
            ))}
        </ProductList>

        <InfoGrid>
          {/* Payment Information */}
          <InfoCard>
            <SectionTitle>Payment Details</SectionTitle>
            <InfoSection>
              <div className="info-row">
                <span className="label">Payment Method</span>
                <span className="value">{order?.paymentType || 'Not specified'}</span>
              </div>
              <div className="info-row">
                <span className="label">Payment Status</span>
                <span
                  className={`value ${order?.paymentStatus?.toLowerCase() === 'success'
                    ? 'text-green-600'
                    : order?.paymentStatus?.toLowerCase() === 'pending'
                      ? 'text-yellow-600'
                      : 'text-red-600'
                    }`}>
                  {order?.paymentStatus || 'Unknown'}
                </span>
              </div>
              <div className="info-row">
                <span className="label">Subtotal</span>
                <span className="value">
                  {formatCurrency(
                    order?.products?.reduce((sum: number, item: any) => {
                      const price = item?.price || item?.bulkOrderPrice || 0;
                      if (price === 0) {
                        const totalQty = order?.products?.reduce((q: number, p: any) => q + (p?.quantity || 0), 0) || 1;
                        const unitPrice = (order?.totalAmount || 0) / totalQty;
                        return sum + (unitPrice * (item?.quantity || 0));
                      }
                      return sum + (price * (item?.quantity || 0));
                    }, 0) || 0
                  )}
                </span>
              </div>
              <div className="info-row">
                <span className="label">Shipping Fee</span>
                <span className="value">{formatCurrency(order?.shippingFee || 0)}</span>
              </div>
              <div className="info-row total-row">
                <span className="label">Total</span>
                <span className="value">{formatCurrency((order?.totalAmount || 0) + (order?.shippingFee || 0))}</span>
              </div>
            </InfoSection>
          </InfoCard>

          {/* Delivery Information */}
          <InfoCard>
            <SectionTitle>Delivery Details</SectionTitle>
            <InfoSection>
              <div className="info-row">
                <span className="label">Delivery Method</span>
                <span className="value">
                  {order?.deliveryMethod === 'pickup'
                    ? 'Pickup'
                    : order?.deliveryMethod === 'freedelivery'
                      ? 'Free Delivery'
                      : 'Home Delivery'}
                </span>
              </div>
              {order?.deliverySchedule && (
                <div className="info-row">
                  <span className="label">Schedule</span>
                  <span className="value">{order.deliverySchedule}</span>
                </div>
              )}
              {order?.deliveryStatus && order?.orderStatus?.toUpperCase() !== 'CANCELLED' && (
                <div className="info-row">
                  <span className="label">Delivery Status</span>
                  <span className="value capitalize">{order.deliveryStatus}</span>
                </div>
              )}
              {order?.dateOfSchedule && (
                <div className="info-row">
                  <span className="label">Expected Date</span>
                  <span className="value">{moment(order.dateOfSchedule).format('DD/MM/YYYY')}</span>
                </div>
              )}
            </InfoSection>

            {/* Vendor Information */}
            {order?.vendorName && (
              <>
                <SectionTitle style={{ marginTop: '16px' }}>Vendor</SectionTitle>
                <div style={{ marginBottom: '8px', fontWeight: '600' }}>{order.vendorName}</div>
                {order?.vendorAddress && (
                  <AddressInfo>
                    {order.vendorAddress.street && <div>{order.vendorAddress.street}</div>}
                    {order.vendorAddress.state && <div>{order.vendorAddress.state}</div>}
                    {order.vendorAddress.country && <div>{order.vendorAddress.country}</div>}
                  </AddressInfo>
                )}
              </>
            )}
          </InfoCard>
        </InfoGrid>

        <ActionButtons>
          {order?.orderStatus?.toUpperCase() !== 'CANCELLED' && order?.deliveryMethod !== 'pickup' && (
            <OrderButton outlined color={COLORS.MainBlue} onClick={track}>
              Track Order
            </OrderButton>
          )}
          {canCancelOrder() && (
            <OrderButton outlined color="#F04438" onClick={() => setDecline(true)}>
              Cancel Order
            </OrderButton>
          )}
        </ActionButtons>
      </OrderDetailsContainer>

      {/* Cancel Order Modal */}
      {isDecline && (
        <Modal maxWidth="400px" onModalClose={() => setDecline(false)}>
          <div className="flex items-center w-full flex-col gap-5">
            <p>Are you sure you want to cancel your order?</p>
            <TextField
              rows={5}
              multiline
              fullWidth
              value={reason}
              onChange={e => setReason(e.currentTarget.value)}
              placeholder="Why do you want to cancel your order"
              required
            />
            <span className="text-red-600 text-sm">{err}</span>
            <div className="flex items-center gap-2">
              <OrderButton onClick={() => setDecline(false)} outlined color="#808080">
                Keep Order
              </OrderButton>
              <OrderButton
                className="disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:opacity-30"
                disabled={reason.length < 2}
                onClick={handleCancel}
                outlined
                color="#F04438">
                Cancel Order
              </OrderButton>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};
